import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { PortsideStripe, PortsideMark } from "@/components/Brand";
import { Anchor, Shield, BarChart3 } from "lucide-react";
import PreviewBanner from "@/components/PreviewBanner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.75h3.57c2.08-1.92 3.28-4.74 3.28-8.07z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.75c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.12c-.22-.66-.35-1.36-.35-2.12s.13-1.46.35-2.12V7.04H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.96l3.66-2.84z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.04l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
  </svg>
);

export default function Login() {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;

  const errorMsg = new URLSearchParams(location.search).get("error");

  const handleGoogleLogin = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/inspection/entry";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <PreviewBanner />
      <PortsideStripe />
      <div className="flex-1 grid lg:grid-cols-2">
        {/* Left pane: imagery + brand statement */}
        <aside className="relative hidden lg:flex flex-col justify-between overflow-hidden bg-[#0a2952]" data-testid="login-left-pane">
          <img
            src="https://images.unsplash.com/photo-1735048002878-da85b65b96b3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHw0fHxzaGlwcGluZyUyMGNvbnRhaW5lcnMlMjBwb3J0JTIwY2FyZ28lMjBzaGlwfGVufDB8fHx8MTc3ODIzNDUxMHww&ixlib=rb-4.1.0&q=85"
            alt="Shipping containers"
            className="absolute inset-0 w-full h-full object-cover opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a2952] via-[#0a2952]/85 to-[#0a2952]/40" />
          <div className="relative z-10 p-10 flex items-center gap-3">
            <span className="portside-wordmark text-white" style={{ color: "#fff" }}>portside</span>
            <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/60 border-l border-white/30 pl-3">
              Container Operations
            </span>
          </div>
          <div className="relative z-10 p-10 lg:p-14 max-w-xl">
            <div className="overline text-[#ff5b2c] mb-4">Port of Tema · Ghana</div>
            <h1 className="font-display font-extrabold text-white text-4xl lg:text-5xl leading-[1.05] tracking-tight mb-6">
              HPL empties.<br />
              <span className="text-[#ff5b2c]">Tracked. Charged. Cleared.</span>
            </h1>
            <p className="text-white/80 text-base leading-relaxed mb-8 max-w-md">
              The dedicated control room for direct gate-in operations at MTG — inspection,
              service charges, toll & vann, and every container that crosses the gate.
            </p>
            <div className="grid grid-cols-3 gap-6 max-w-md">
              <Stat label="Containers / Mo" value="1,497" />
              <Stat label="Gate Locations" value="2" />
              <Stat label="Charge Currencies" value="USD · GHC" />
            </div>
          </div>
          <div className="relative z-10 p-10 flex items-center gap-2 text-xs text-white/50">
            <Anchor size={14} /> Multipurpose Terminals Ghana Ltd
          </div>
        </aside>

        {/* Right pane: form */}
        <section className="flex items-center justify-center p-8 lg:p-16">
          <div className="w-full max-w-md">
            <div className="lg:hidden mb-8 flex items-center gap-3">
              <PortsideMark />
              <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-slate-400 border-l border-slate-200 pl-3">
                Container Operations
              </span>
            </div>
            <div className="overline mb-3">Sign in</div>
            <h2 className="font-display font-extrabold text-3xl text-[#0a2952] tracking-tight mb-3">
              Welcome aboard.
            </h2>
            <p className="text-slate-600 mb-8 leading-relaxed">
              Sign in with your Google account to access the container operations dashboard.
              Your team&apos;s data is protected behind authentication.
            </p>

            {errorMsg && (
              <div className="mb-6 px-4 py-3 rounded-md border border-red-200 bg-red-50 text-sm text-red-700" data-testid="login-error">
                Sign-in failed. Please try again.
              </div>
            )}

            <button
              type="button"
              data-testid="google-login-btn"
              onClick={handleGoogleLogin}
              className="w-full inline-flex items-center justify-center gap-3 bg-white border border-slate-300 hover:border-[#0a2952] hover:bg-slate-50 text-slate-800 rounded-md px-4 py-3.5 font-semibold shadow-sm transition-all hover:-translate-y-[1px] hover:shadow"
            >
              <GoogleIcon />
              <span>Continue with Google</span>
            </button>

            <div className="mt-10 grid grid-cols-1 gap-4">
              <Bullet icon={<Shield size={16} />} title="Secure" desc="Session-cookie auth, 7-day expiry, server-verified." />
              <Bullet icon={<BarChart3 size={16} />} title="Live operational view" desc="KPIs, charges, and container records in one place." />
              <Bullet icon={<Anchor size={16} />} title="Built for Tema" desc="Designed around HPL gate-in flows at MTG and GPHA." />
            </div>

            <p className="text-[11px] text-slate-400 mt-10">
              By continuing, you agree to Portside&apos;s terms of use. Your Google account is used solely to verify your identity.
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-white font-display font-extrabold text-2xl leading-none">{value}</div>
      <div className="mt-2 text-[10px] uppercase tracking-[0.18em] text-white/60 font-semibold">{label}</div>
    </div>
  );
}

function Bullet({ icon, title, desc }) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 w-8 h-8 rounded-md bg-[#0a2952]/5 text-[#0a2952] grid place-items-center">{icon}</div>
      <div>
        <div className="text-sm font-semibold text-slate-800">{title}</div>
        <div className="text-xs text-slate-500 leading-relaxed">{desc}</div>
      </div>
    </div>
  );
}
